import React, { useEffect, useState } from "react";
import { api, inrPlain, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useGroup } from "@/context/GroupContext";
import { useNavigate } from "react-router-dom";
import { ListSkeleton } from "@/components/Skeletons";
import { ArrowUpRight, ArrowDownRight, Search, UserPlus, Shield, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Members() {
  const { user } = useAuth();
  const { slug, groupId, isAdmin, group, refresh } = useGroup();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    if (!groupId) return;
    api.get("/dashboard", { params: { group_id: groupId } }).then((r) => setData(r.data));
  };

  useEffect(() => { load(); }, [groupId]);

  if (!data) return <ListSkeleton rows={4} />;

  const filtered = data.people.filter((p) => p.user.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-5 lg:max-w-4xl">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Balances</div>
          <h1 className="heading text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-1">People</h1>
        </div>
        {isAdmin && (
          <Button
            onClick={() => setAddOpen(true)}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-11 px-4"
            data-testid="members-add-button"
          >
            <UserPlus className="w-4 h-4 mr-1.5" /> Add member
          </Button>
        )}
      </div>

      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
        <Input
          placeholder="Search members"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-12 pl-10 rounded-2xl bg-white"
          data-testid="members-search"
        />
      </div>

      <div className="space-y-3">
        {filtered.map(({ user: u, balance }) => {
          const pos = balance > 0.005;
          const neg = balance < -0.005;
          const isMemberAdmin = group?.admin_id === u.id;
          return (
            <div
              key={u.id}
              className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-slate-200 hover:border-slate-300"
              data-testid={`member-row-${u.id}`}
            >
              <button
                onClick={() => navigate(`/${slug}/members/${u.id}`)}
                className="flex items-center gap-3 flex-1 text-left"
              >
                <div className="w-11 h-11 rounded-full overflow-hidden ring-1 ring-slate-200">
                  {u.photo && <img src={u.photo} alt={u.name} className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-slate-900">{u.name}</div>
                    {isMemberAdmin && (
                      <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold flex items-center gap-1">
                        <Shield className="w-2.5 h-2.5" /> Admin
                      </span>
                    )}
                  </div>
                  <div className={`text-xs mt-0.5 font-semibold flex items-center gap-1 ${pos ? "text-emerald-600" : neg ? "text-rose-600" : "text-slate-400"}`}>
                    {pos ? <ArrowDownRight className="w-3 h-3" /> : neg ? <ArrowUpRight className="w-3 h-3" /> : null}
                    {pos ? "owes you" : neg ? "you owe" : "settled"}
                  </div>
                </div>
                <div className={`amount font-bold ${pos ? "text-emerald-600" : neg ? "text-rose-600" : "text-slate-400"}`}>
                  {inrPlain(Math.abs(balance))}
                </div>
              </button>
              {isAdmin && !isMemberAdmin && (
                <RemoveMemberButton groupId={groupId} userId={u.id} onDone={() => { load(); refresh(); }} />
              )}
            </div>
          );
        })}
      </div>

      <AddMemberDialog open={addOpen} onOpenChange={setAddOpen} groupId={groupId} onSuccess={() => { load(); refresh(); }} />
    </div>
  );
}

function RemoveMemberButton({ groupId, userId, onDone }) {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    if (!window.confirm("Remove this member? Their historical entries stay in the ledger.")) return;
    setBusy(true);
    try {
      await api.delete(`/groups/${groupId}/members/${userId}`);
      toast.success("Member removed");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={remove}
      disabled={busy}
      className="w-9 h-9 rounded-full hover:bg-rose-50 grid place-items-center text-slate-400 hover:text-rose-600"
      data-testid={`member-remove-${userId}`}
      aria-label="Remove"
    >
      <Trash2 className="w-4 h-4" />
    </button>
  );
}

function AddMemberDialog({ open, onOpenChange, groupId, onSuccess }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mobile, setMobile] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (!open) { setName(""); setEmail(""); setPassword(""); setMobile(""); }
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim() || !email.trim() || password.length < 6) {
      toast.error("Name, email, and a 6+ char password are required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/groups/${groupId}/members`, { name, email, password, mobile });
      toast.success(`${name} added. Share their credentials so they can sign in.`);
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="add-member-dialog">
        <DialogHeader>
          <DialogTitle className="heading text-2xl">Add member</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <p className="text-xs text-slate-500">
            You'll set their login credentials. Share them privately (WhatsApp/email) so they can sign in.
          </p>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Full name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-2 h-12 rounded-2xl" required data-testid="add-member-name" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 h-12 rounded-2xl" required data-testid="add-member-email" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Temporary password (min 6)</Label>
            <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 h-12 rounded-2xl font-mono" minLength={6} required data-testid="add-member-password" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Mobile (optional)</Label>
            <Input value={mobile} onChange={(e) => setMobile(e.target.value)} className="mt-2 h-12 rounded-2xl" data-testid="add-member-mobile" />
          </div>
          <Button
            type="submit"
            disabled={busy}
            className="w-full h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
            data-testid="add-member-submit"
          >
            {busy ? "Adding…" : "Add member"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
